/* global Symbol Map */

// Note: this module depends on src/component/global/polyfill being loaded

// Local imports
import { enum_value } from '../util/enumtool.js';
import { PAGE_VARS } from '../page/vars.js';

// ===========================================================================
// Globals
// ===========================================================================

const EVENT = new Map();

export const EventKind = {
    Impression: Symbol('impression'),
    ProductDetail: Symbol('product_detail'),
    DOMReady: Symbol('domready'),
    PromoClick: Symbol('promo_click'),
    ProductClick: Symbol('product_click'),
    ProductClickCartAdd: Symbol('product_click_cartadd'),
    AddToCart: Symbol('add_to_cart'),
    AddToCartError: Symbol('add_to_cart_error'),
    ToCheckout: Symbol('to_checkout'),
    ToCheckoutStepTwo: Symbol('to_checkout_step2'),
    ToCheckoutStepTwoError: Symbol('to_checkout_step2_error'),
    ToCheckoutStepThree: Symbol('to_checkout_step3'),
    ToCheckoutStepThreeError: Symbol('to_checkout_step3_error'),
    RemoveFromCart: Symbol('remove_from_cart'),
};

// ===========================================================================
//
// ===========================================================================

// Only call global.procEvent if it exists and is a function
function trigger_event(global, cmd, category) {
    // Wait 0.5 seconds before trying to call global.procEvent again
    if (typeof global.procEvent !== 'function') {
        setTimeout(() => {
            trigger_event(global, cmd, category);
        }, 500);
    } else {
        global.procEvent(cmd, category);
    }
}

function init_event_listener(global, events) {
    for (let event of events.values()) {
        global.document.addEventListener(event.type, () => {
            let args = [global];
            args.push.apply(args, event.detail.args);
            trigger_event.apply(null, args);
        });
    }
}

function create_event(name, label, category) {
    const event = new CustomEvent(name, {
        detail: {
            args: [label, category],
        },
    });

    return event;
}

export function init_events(global) {
    const impression = create_event(
        'update_datalayer_impression',
        'product impressions update',
        'ecommerce'
    );
    const product_detail = create_event(
        'update_datalayer_product',
        'product details update',
        'ecommerce'
    );
    const domready = create_event(
        'domready',
        'UDO_dom_ready',
        enum_value(PAGE_VARS.kind)
    );
    const promo_click = create_event(
        'update_datalayer_promoclick',
        'promo click',
        'ecommerce'
    );
    const product_click = create_event(
        'update_datalayer_productclick',
        'product click',
        'ecommerce'
    );
    const product_click_cartadd = create_event(
        'update_datalayer_product_click_cartadd',
        'product click - add to cart',
        'ecommerce'
    );
    const add_to_cart = create_event(
        'update_datalayer_add2cart',
        'add to cart',
        'ecommerce'
    );
    const add_to_cart_error = create_event(
        'update_datalayer_add2cart_error',
        'add to cart',
        'form error'
    );
    const to_checkout = create_event(
        'update_datalayer_to_checkout1',
        'checkout step 1',
        'ecommerce'
    );
    const to_checkout2 = create_event(
        'update_datalayer_to_checkout2',
        'checkout step 2',
        'ecommerce'
    );
    const to_checkout2_error = create_event(
        'update_datalayer_to_checkout2_error',
        'checkout step 2',
        'form error'
    );
    const to_checkout3 = create_event(
        'update_datalayer_to_checkout3',
        'checkout step 3',
        'ecommerce'
    );
    const to_checkout3_error = create_event(
        'update_datalayer_to_checkout3_error',
        'checkout step 3',
        'form error'
    );
    const remove_from_cart = create_event(
        'update_datalayer_remove_from_cart',
        'remove from cart',
        'ecommerce'
    );

    EVENT.set(EventKind.Impression, impression);
    EVENT.set(EventKind.ProductDetail, product_detail);
    EVENT.set(EventKind.DOMReady, domready);
    EVENT.set(EventKind.PromoClick, promo_click);
    EVENT.set(EventKind.ProductClick, product_click);
    EVENT.set(EventKind.ProductClickCartAdd, product_click_cartadd);
    EVENT.set(EventKind.AddToCart, add_to_cart);
    EVENT.set(EventKind.AddToCartError, add_to_cart_error);
    EVENT.set(EventKind.ToCheckout, to_checkout);
    EVENT.set(EventKind.ToCheckoutStepTwo, to_checkout2);
    EVENT.set(EventKind.ToCheckoutStepTwoError, to_checkout2_error);
    EVENT.set(EventKind.ToCheckoutStepThree, to_checkout3);
    EVENT.set(EventKind.ToCheckoutStepThreeError, to_checkout3_error);
    EVENT.set(EventKind.RemoveFromCart, remove_from_cart);

    init_event_listener(global, EVENT);

    return EVENT;
}

export function dispatch(global, name) {
    let event = EVENT.get(name);
    if (typeof event === 'undefined') {
        let name_val;
        if (typeof name === 'symbol') {
            name_val = name.toString();
        } else {
            name_val = new String(name);
        }
        throw new Error(`Unknown event: ${name_val}`);
    }
    global.document.dispatchEvent(event);
}

// ===========================================================================
//
// ===========================================================================
